import shopifyConfigApi from 'shopifyConfig/gateways/shopifyConfig-api';
import commonApi from 'common/gateways/common-api';
import { handleApiCall } from 'utils/handleApiCallController.js';
import { EVENTS } from 'shopifyConfig/controllers/events.js';
import {
  buildUpdatePayload,
  getValidCustomizations,
  syncCustomizationSchema,
} from 'pages/shopifyConfig/helpers/customisationHelpers';
import { CACHE_KEY, CACHE_EXPIRY_MS } from 'shopifyConfig/controllers/constants';
import { getCachedData, setCachedData } from 'utils/cacheUtils';

export async function getDashboardData(eventEmitter, params) {
  const cached = getCachedData(CACHE_KEY, CACHE_EXPIRY_MS);
  if (cached) {
    eventEmitter.emit('GET_SHOPIFY_DASHBOARD_DATA_SUCCESS', cached);
    return;
  }
  try {
    const data = await shopifyConfigApi.getDashboardData(params);
    setCachedData(CACHE_KEY, data);
    eventEmitter.emit('GET_SHOPIFY_DASHBOARD_DATA_SUCCESS', data);
  } catch (error) {
    eventEmitter.emit('GET_SHOPIFY_DASHBOARD_DATA_FAILURE');
  }
}

/**
 * Sends a request to close the Shopify embedded app window by calling the specified Shopify middleware URL.
 * This function sends a GET request to the `/connect` endpoint with the provided parameters.
 *
 * @param {Object} params - Parameters for the API request to close the Shopify window.
 * @param {string} params.id - A unique identifier for the session or operation.
 * @param {string} params.ref_id - A reference ID to track the specific operation, typically for logging purposes.
 * @param {string} params.shop - The subdomain of the Shopify store (e.g., 'test-paypal01') that will be used to identify the store.
 *
 * @returns {void} This function does not return any value. It performs the API call and resolves once completed.
 * The API request failure or success is handled by the underlying HTTP client.
 *
 * @throws {Error} The function does not explicitly throw errors. Any errors encountered during the API call
 * will be handled by the underlying HTTP client, and the promise will be rejected.
 */
export async function closeShopifyWindow(eventEmitter, params) {
  await handleApiCall(
    async () => {
      await shopifyConfigApi.closeShopifyWindow(params);
    },
    eventEmitter,
    EVENTS?.SHOPIFY_CLOSED_FAILURE,
  );
}

export async function installShopifyWindow(eventEmitter, params) {
  return await handleApiCall(
    async () => {
      return await shopifyConfigApi.installShopifyWindow(params);
    },
    eventEmitter,
    EVENTS?.SHOPIFY_CLOSED_FAILURE,
  );
}
const buildSyncedData = (data, newSettings) => ({
  ...data,
  settings: {
    ...data.settings,
    dynamic: {
      ...data.settings?.dynamic,
      clientData: {
        ...data.settings?.dynamic?.clientData,
        customization_settings: newSettings,
      },
    },
  },
});

export const getCustomizations = async (business, pluginRefId) => {
  return shopifyConfigApi.listCustomizations(business, pluginRefId);
};

// Fetches customizations, reports whether any valid (renderable) ones exist, and
// emits the result over the eventEmitter. `data` is forwarded so the subscriber can
// continue its GET_DATA flow once verification completes. Failures are treated as
// "no valid customizations" so the UI still resolves.
export const verifyCustomizations = async ({ business, pluginRefId, data, eventEmitter }) => {
  let apiCustomizations = {};
  try {
    apiCustomizations = (await getCustomizations(business, pluginRefId)) || {};
  } catch (error) {
    console.error('Failed to verify customizations', error);
  }

  if (eventEmitter) {
    eventEmitter.emit(EVENTS.VERIFY_CUSTOMIZATIONS_SUCCESS, {
      data,
      apiCustomizations,
      hasValidCustomizations: getValidCustomizations(apiCustomizations).length > 0,
    });
  }
};

const syncCustomizations = async ({ apiCustomizations, business, data, id, eventEmitter }) => {
  const currentSettings = data?.settings?.dynamic?.clientData?.customization_settings || {};
  const { hasChanges, newSettings } = syncCustomizationSchema(apiCustomizations, currentSettings);

  if (!hasChanges) return;

  const tempValues = {
    customization_settings: newSettings,
    pro_discount_codes: data?.settings?.dynamic?.clientData?.pro_discount_codes || [],
  };
  const payload = buildUpdatePayload(tempValues, data, id);
  const updatedData = buildSyncedData(data, newSettings);

  try {
    await commonApi.updateData({ business, payload });
  } catch (e) {
    // Emit error but still forward updatedData so the UI renders correctly for this session.
    if (eventEmitter) eventEmitter.emit(EVENTS.SYNC_CUSTOMIZATIONS_ERROR, { error: e, updatedData });
    return;
  }

  // Update local state directly — avoids re-emitting GET_DATA_SUCCESS
  // which would trigger a redundant getCustomizations() call in index.js.
  if (eventEmitter) eventEmitter.emit(EVENTS.SYNC_CUSTOMIZATIONS_SUCCESS, { updatedData });
};

export const submitCustomizations = ({ values, data, id, handleUpdateData }) => {
  const payload = buildUpdatePayload(values, data, id);
  handleUpdateData(payload);
};

export const loadCustomizations = async ({ business, data, id, eventEmitter }) => {
  try {
    const result = await getCustomizations(business, data?.pluginRefId);
    const validCustomizations = getValidCustomizations(result);

    if (eventEmitter) {
      eventEmitter.emit(EVENTS.FETCH_CUSTOMIZATIONS_SUCCESS, {
        apiCustomizations: result || {},
        hasValidCustomizations: validCustomizations.length > 0,
      });
    }

    if (validCustomizations.length > 0) {
      await syncCustomizations({ apiCustomizations: result, business, data, id, eventEmitter });
    }
  } catch (e) {
    if (eventEmitter) eventEmitter.emit(EVENTS.FETCH_CUSTOMIZATIONS_ERROR, e);
  }
};
